// Contrat des actions sociales (M20, lot L7, D-280, D-282 à D-287). Module optionnel, désactivé par défaut et activé
// seulement par le maire ou président. Rôle et habilitation nominative « social » sont cumulatifs sur chaque
// lecture, écriture, pièce, export et recherche (D-023). Programmes à critères versionnés, demandes, évaluation
// explicable des critères (proposition, jamais décision), doublons examinés par un humain, décision motivée par
// une personne distincte de l'instructeur, référence à un engagement de dépense existant, suivi. Aucune règle
// officielle ni montant n'est prérempli : les exemples du générateur sont fictifs.
import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const texte = (max: number, min = 1) => z.string().trim().min(min).max(max);
const facultatif = (max: number) => z.string().trim().max(max).optional();
const code = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9_]{1,40}$/, 'Code en minuscules, chiffres et soulignés');
const montant = z.number().int('Montant entier en francs CFA').positive().max(10_000_000_000);

export const TYPES_AIDE_SOCIALE = {
  financiere: 'Aide financière',
  nature: 'Aide en nature',
  accompagnement: 'Accompagnement',
} as const;
export type TypeAideSociale = keyof typeof TYPES_AIDE_SOCIALE;

export const ETATS_PROGRAMME_SOCIAL = {
  brouillon: 'En préparation',
  ouvert: 'Ouvert aux demandes',
  clos: 'Clos',
} as const;
export type EtatProgrammeSocial = keyof typeof ETATS_PROGRAMME_SOCIAL;

export const ETATS_DEMANDE_SOCIALE = {
  recue: 'Reçue',
  en_instruction: 'En instruction',
  a_decider: 'À décider',
  accordee: 'Accordée',
  refusee: 'Refusée',
  classee: 'Classée sans suite',
  cloturee: 'Suivi clôturé',
} as const;
export type EtatDemandeSociale = keyof typeof ETATS_DEMANDE_SOCIALE;

/** Libellés montrés à l'usager : aucun détail d'instruction. */
export const ETATS_DEMANDE_SOCIALE_USAGER: Record<EtatDemandeSociale, string> = {
  recue: 'Reçue',
  en_instruction: "En cours d'examen",
  a_decider: "En cours d'examen",
  accordee: 'Suite favorable',
  refusee: 'Suite défavorable',
  classee: 'Classée sans suite',
  cloturee: 'Terminée',
};

export const NATURES_CRITERE_SOCIAL = {
  declaration: 'Déclaration du demandeur',
  piece: 'Pièce justificative',
  constat: "Constat de l'agent",
} as const;
export const CONSTATS_CRITERE = {
  satisfait: 'Satisfait',
  non_satisfait: 'Non satisfait',
  a_verifier: 'À vérifier',
} as const;
export type ConstatCritere = keyof typeof CONSTATS_CRITERE;

export const PROPOSITIONS_ELIGIBILITE = {
  eligible_propose: 'Critères obligatoires satisfaits (proposition)',
  non_eligible_propose: 'Au moins un critère obligatoire non satisfait (proposition)',
  incomplete: 'Évaluation incomplète',
} as const;
export type PropositionEligibilite = keyof typeof PROPOSITIONS_ELIGIBILITE;

export const INDICES_DOUBLON = {
  meme_identite: 'Même nom, prénoms et date de naissance',
  meme_nom: 'Même nom et prénoms (homonymie possible, date de naissance différente ou absente)',
  meme_telephone: 'Même numéro de téléphone',
} as const;
export type IndiceDoublon = keyof typeof INDICES_DOUBLON;

export const EXAMENS_DOUBLON = {
  a_examiner: 'À examiner',
  doublon_confirme: 'Doublon confirmé',
  non_doublon: 'Pas un doublon',
} as const;
export type ExamenDoublon = keyof typeof EXAMENS_DOUBLON;

export const NATURES_SUIVI_SOCIAL = {
  versement_constate: 'Versement constaté (chaîne de la dépense)',
  remise_en_nature: 'Remise en nature',
  entretien: 'Entretien',
  visite: 'Visite',
  autre: 'Autre',
} as const;
export type NatureSuiviSocial = keyof typeof NATURES_SUIVI_SOCIAL;

/** Activation explicite du module par le maire ou président (jamais automatique). */
export const schemaActivationSociale = z.strictObject({ actif: z.boolean(), motif: texte(500, 3) });
export interface ActivationSociale {
  actif: boolean;
  peut_modifier: boolean;
  active_le: string | null;
  desactive_le: string | null;
  motif: string | null;
}

// --- Programmes et critères versionnés (social : administrer) --------------------------------------------

export const schemaCritereSocial = z.strictObject({
  code,
  libelle: texte(200, 3),
  nature: z.enum(['declaration', 'piece', 'constat']),
  obligatoire: z.boolean().default(true),
  aide: facultatif(500),
});
export type CritereSocial = z.infer<typeof schemaCritereSocial>;

const criteres = z
  .array(schemaCritereSocial)
  .min(1)
  .max(20)
  .refine((l) => new Set(l.map((c) => c.code)).size === l.length, {
    message: 'Chaque critère a un code distinct',
  });

export const schemaCreationProgrammeSocial = z
  .strictObject({
    code,
    intitule: texte(200, 3),
    objet: texte(1000, 3),
    type_aide: z.enum(['financiere', 'nature', 'accompagnement']),
    debut: jour,
    fin: jour.optional(),
    /** Enveloppe indicative en francs CFA : aucun versement n'en découle. */
    enveloppe_indicative: montant.optional(),
    /** Source administrative (délibération, arrêté) : rien n'est présumé par l'application. */
    reference_acte: texte(300, 3),
    criteres,
    fictif: z.boolean().default(false),
  })
  .refine((p) => !p.fin || p.fin >= p.debut, { message: 'La fin suit le début', path: ['fin'] });
export type CreationProgrammeSocial = z.infer<typeof schemaCreationProgrammeSocial>;

export const schemaVersionCriteresSocial = z.strictObject({ criteres, motif: texte(500, 3) });
export type VersionCriteresSocial = z.infer<typeof schemaVersionCriteresSocial>;

export const schemaEtatProgrammeSocial = z.strictObject({
  etat: z.enum(['ouvert', 'clos']),
  motif: texte(500, 3),
});
export type ChangementEtatProgrammeSocial = z.infer<typeof schemaEtatProgrammeSocial>;

// --- Demandes (social : creer, modifier, transmettre, decider) -------------------------------------------

export const schemaBeneficiaireSocial = z.strictObject({
  nom: texte(120),
  prenoms: facultatif(160),
  date_naissance: jour.optional(),
  telephone: z
    .string()
    .trim()
    .regex(/^\+?[0-9 ]{6,20}$/, 'Numéro de téléphone invalide')
    .optional(),
  quartier: facultatif(160),
  /** Compte usager du portail lié (identifiant de connexion), pour l'état public de la demande. */
  identifiant_usager: facultatif(200),
});
export type BeneficiaireSocialSaisi = z.infer<typeof schemaBeneficiaireSocial>;

export const schemaCreationDemandeSociale = z.strictObject({
  programme_id: uuid,
  beneficiaire: schemaBeneficiaireSocial,
  /** Situation exposée : chiffrée en base, jamais dans le journal, les notifications ni le portail. */
  situation: texte(4000, 10),
  montant_demande: montant.optional(),
});
export type CreationDemandeSociale = z.infer<typeof schemaCreationDemandeSociale>;

export const schemaEvaluationCriteres = z.strictObject({
  evaluations: z
    .array(
      z.strictObject({
        code,
        constat: z.enum(['satisfait', 'non_satisfait', 'a_verifier']),
        commentaire: facultatif(500),
      }),
    )
    .min(1)
    .max(20)
    .refine((l) => new Set(l.map((e) => e.code)).size === l.length, {
      message: 'Un critère est évalué une fois',
    }),
});
export type EvaluationCriteres = z.infer<typeof schemaEvaluationCriteres>;

export const schemaNoteSociale = z.strictObject({ contenu: texte(4000, 3) });
export const schemaExamenDoublon = z.strictObject({
  conclusion: z.enum(['doublon_confirme', 'non_doublon']),
  motif: texte(500, 3),
});
export const schemaMotifSocial = z.strictObject({ motif: texte(500, 3) });
export const schemaMessageUsagerSocial = z.strictObject({ message_usager: texte(1000, 3) });

export const schemaDecisionSociale = z
  .strictObject({
    sens: z.enum(['accord', 'refus']),
    /** Motif interne de la décision (chiffré) : toujours exigé, même pour un accord. */
    motif: texte(2000, 10),
    nature_aide: facultatif(300),
    montant_accorde: montant.optional(),
    /** Texte explicitement destiné à l'usager (portail) ; rien d'autre ne lui est montré. */
    message_usager: facultatif(1000),
  })
  .refine((d) => d.sens === 'refus' || Boolean(d.nature_aide), {
    message: "Un accord précise la nature de l'aide",
    path: ['nature_aide'],
  })
  .refine((d) => d.sens === 'accord' || d.montant_accorde === undefined, {
    message: 'Un refus ne porte pas de montant',
    path: ['montant_accorde'],
  });
export type DecisionSocialeSaisie = z.infer<typeof schemaDecisionSociale>;

export const schemaReferenceDepenseSociale = z.strictObject({ engagement_id: uuid });

export const schemaSuiviSocial = z.strictObject({
  nature: z.enum(['versement_constate', 'remise_en_nature', 'entretien', 'visite', 'autre']),
  date_suivi: jour,
  observation: texte(2000, 3),
  cloturer: z.boolean().default(false),
});
export type SuiviSocialSaisi = z.infer<typeof schemaSuiviSocial>;

export const schemaFiltreDemandesSociales = z.strictObject({
  programme_id: uuid.optional(),
  etat: z
    .enum(['recue', 'en_instruction', 'a_decider', 'accordee', 'refusee', 'classee', 'cloturee'])
    .optional(),
  recherche: facultatif(80),
});
export type FiltreDemandesSociales = z.infer<typeof schemaFiltreDemandesSociales>;

// --- Réponses -------------------------------------------------------------------------------------------

export interface VersionCriteres {
  id: string;
  version: number;
  criteres: CritereSocial[];
  motif: string;
  publie_le: string;
}

export interface ProgrammeSocial {
  id: string;
  code: string;
  intitule: string;
  objet: string;
  type_aide: TypeAideSociale;
  debut: string;
  fin: string | null;
  enveloppe_indicative: number | null;
  reference_acte: string;
  etat: EtatProgrammeSocial;
  fictif: boolean;
  version_courante: VersionCriteres;
  versions: number;
  demandes: number;
}

export interface ResumeDemandeSociale {
  id: string;
  numero: string;
  programme: { id: string; code: string; intitule: string };
  beneficiaire: string;
  etat: EtatDemandeSociale;
  recue_le: string;
  doublons_a_examiner: number;
  fictif: boolean;
}

export interface ExplicationEligibilite {
  resultat: PropositionEligibilite;
  explications: string[];
}

export interface DoublonSocial {
  id: string;
  demande: { id: string; numero: string; etat: EtatDemandeSociale; programme: string };
  indices: { code: IndiceDoublon; libelle: string }[];
  examen: ExamenDoublon;
  motif: string | null;
  examine_par: string | null;
  examine_le: string | null;
}

export interface PieceSociale {
  id: string;
  type_piece: string;
  type_mime: string;
  taille: number;
  empreinte_sha256: string;
  deposee_le: string;
}

export interface DecisionSociale {
  sens: 'accord' | 'refus';
  motif: string;
  nature_aide: string | null;
  montant_accorde: number | null;
  message_usager: string | null;
  criteres_version: number;
  proposition_au_moment: PropositionEligibilite;
  decide_par: string;
  decide_le: string;
  engagement: { id: string; numero: string | null; etat: string; montant: number } | null;
}

export interface DemandeSociale extends ResumeDemandeSociale {
  beneficiaire_detail: {
    nom: string;
    prenoms: string | null;
    date_naissance: string | null;
    telephone: string | null;
    quartier: string | null;
    usager_lie: boolean;
  };
  situation: string;
  montant_demande: number | null;
  criteres: VersionCriteres;
  evaluations: { code: string; constat: ConstatCritere; commentaire: string | null }[];
  proposition: ExplicationEligibilite;
  doublons: DoublonSocial[];
  notes: { id: string; contenu: string; auteur: string; cree_le: string }[];
  pieces: PieceSociale[];
  decision: DecisionSociale | null;
  suivis: {
    id: string;
    nature: NatureSuiviSocial;
    date_suivi: string;
    observation: string;
    auteur: string;
    cree_le: string;
  }[];
  message_usager: string | null;
  instruction: { transmise_par: string | null; transmise_le: string | null };
  historique: { horodatage: string; libelle: string; auteur: string | null }[];
}

/** Agrégats sans donnée individuelle : effectifs inférieurs à cinq masqués (règle D-271 reprise). */
export interface SyntheseSociale {
  module_actif: boolean;
  seuil_masquage: number;
  programmes: {
    code: string;
    intitule: string;
    etat: EtatProgrammeSocial;
    demandes: number | null;
    accordees: number | null;
    refusees: number | null;
    en_cours: number | null;
  }[];
}

export interface DemandeSocialeUsager {
  numero: string;
  programme: string;
  etat: string;
  recue_le: string;
  message_usager: string | null;
  collectivite: { code: string; nom: string };
}
